import { useCallback, useEffect, useRef, useState } from "react";
import { Smartphone, X } from "lucide-react";
import { AppStoreBadges } from "@/components/index/AppStoreBadges";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useLanguage } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";
import { indexLabels, commonButtonLabels } from "@/i18n";
import "@/styles/app-download-sheet.css";
import { hasValidCookieConsent } from "@/lib/cookieConsent";

const STORAGE_KEY = "bilenia-app-sheet-dismissed";
const SWIPE_DISMISS_PX = 56;
const SHOW_DELAY_MS = 5000;

/**
 * Top sheet prompting mobile-web visitors to download the native app.
 * Shown once per browser session (sessionStorage). Not shown in Capacitor.
 */
export default function AppDownloadSheet() {
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const { language } = useLanguage();
  const { theme } = useTheme();
  const labels = indexLabels(language);
  const buttons = commonButtonLabels(language);

  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const dragYRef = useRef(0);
  const scrollLockRef = useRef<{ overflow: string; touchAction: string } | null>(
    null,
  );

  useEffect(() => {
    if (isNative || !isMobile) {
      setOpen(false);
      setVisible(false);
      return;
    }
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
    } catch {
      /* ignore */
    }

    // Wait until cookie consent is chosen so sheets don't stack/fight
    if (!hasValidCookieConsent()) {
      const onConsent = () => {
        if (!hasValidCookieConsent()) return;
        window.setTimeout(() => {
          try {
            if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
          } catch {
            /* ignore */
          }
          setOpen(true);
          requestAnimationFrame(() => setVisible(true));
        }, SHOW_DELAY_MS);
      };
      window.addEventListener("bilenia:cookie-consent-changed", onConsent);
      return () =>
        window.removeEventListener("bilenia:cookie-consent-changed", onConsent);
    }

    const delayId = window.setTimeout(() => {
      setOpen(true);
      requestAnimationFrame(() => setVisible(true));
    }, SHOW_DELAY_MS);

    return () => window.clearTimeout(delayId);
  }, [isMobile, isNative]);

  // Lock page scroll while the sheet is open (incl. iOS overscroll)
  useEffect(() => {
    if (!open) return;

    const body = document.body;
    scrollLockRef.current = {
      overflow: body.style.overflow,
      touchAction: body.style.touchAction,
    };
    body.style.overflow = "hidden";
    body.style.touchAction = "none";

    const preventScroll = (e: TouchEvent) => {
      const panel = panelRef.current;
      if (panel && panel.contains(e.target as Node)) return;
      e.preventDefault();
    };

    document.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      const prev = scrollLockRef.current;
      if (prev) {
        body.style.overflow = prev.overflow;
        body.style.touchAction = prev.touchAction;
      }
      document.removeEventListener("touchmove", preventScroll);
    };
  }, [open]);

  const dismiss = useCallback(() => {
    setVisible(false);
    setDragY(0);
    dragYRef.current = 0;
    window.setTimeout(() => {
      setOpen(false);
      try {
        sessionStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
    }, 280);
  }, []);

  // Non-passive listeners so we can preventDefault and stop page scroll while swiping
  useEffect(() => {
    if (!open) return;
    const el = panelRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      startYRef.current = e.touches[0].clientY;
      setIsDragging(true);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startYRef.current == null) return;
      const delta = Math.min(0, e.touches[0].clientY - startYRef.current);
      dragYRef.current = delta;
      setDragY(delta);
      e.preventDefault();
    };

    const onTouchEnd = () => {
      if (startYRef.current == null) return;
      setIsDragging(false);
      if (dragYRef.current <= -SWIPE_DISMISS_PX) {
        dismiss();
      } else {
        dragYRef.current = 0;
        setDragY(0);
      }
      startYRef.current = null;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [open, dismiss]);

  if (!open) return null;

  return (
    <div
      className={`app-download-sheet-root app-download-sheet-root--${theme}`}
      role="dialog"
      aria-modal="true"
      aria-label={labels.hero.appDownloadTitle}
    >
      <button
        type="button"
        className={`app-download-sheet-backdrop app-download-sheet-backdrop--${theme} ${
          visible ? "is-visible" : ""
        }`}
        aria-label={buttons.close}
        onClick={dismiss}
      />
      <div
        ref={panelRef}
        className={`app-download-sheet-panel ${visible ? "is-visible" : ""} ${
          isDragging ? "is-dragging" : ""
        }`}
        style={{
          transform: visible ? `translateY(${dragY}px)` : "translateY(-112%)",
        }}
      >
        <div className={`app-download-sheet-inner app-download-sheet-inner--${theme}`}>
          <div className="app-download-sheet-top">
            <div
              className={`app-download-sheet-icon app-download-sheet-icon--${theme}`}
              aria-hidden
            >
              <Smartphone size={22} strokeWidth={1.75} />
            </div>
            <button
              type="button"
              className={`app-download-sheet-x app-download-sheet-x--${theme}`}
              onClick={dismiss}
              aria-label={buttons.close}
            >
              <X size={18} strokeWidth={2} />
            </button>
          </div>

          <p className={`app-download-sheet-title app-download-sheet-title--${theme}`}>
            {labels.hero.appDownloadTitle}
          </p>
          <p className={`app-download-sheet-desc app-download-sheet-desc--${theme}`}>
            {labels.hero.appDownloadSubtitle}
          </p>

          <div className="app-download-sheet-badges">
            <AppStoreBadges
              appStoreLabel={labels.hero.appStoreLabel}
              appStoreName={labels.hero.appStoreName}
              playStoreLabel={labels.hero.playStoreLabel}
              playStoreName={labels.hero.playStoreName}
            />
          </div>

          <div className="app-download-sheet-handle-wrap" aria-hidden>
            <span className={`app-download-sheet-handle app-download-sheet-handle--${theme}`} />
          </div>
        </div>
      </div>
    </div>
  );
}
